import AdFoxHelper from '../helpers/AdFoxHelper';

const MIN_SCREEN_WIDTH_FOR_SIDEBARS = 1240;
const RESIZE_DEBOUNCE_MS = 300;
const PLACEMENT_TYPES = {
    BR1: 'br1',
    BR2: 'br2'
};
const HAS_PROMO_CLASS = 'has-promo-placements';

class PromoPlacementManager {
    constructor() {
        this.currentCountryId = null;
        this.activePlacements = [];
        this.promoPlacements = [];
        this.adFoxHelper = AdFoxHelper.getInstance();
        this.ownerId = null;
        this.resizeDebounceTimer = null;
        this.resizeDebounceDelay = RESIZE_DEBOUNCE_MS;
        this.isResizeListenerAdded = false;
        this.resizeHandler = null;
        this.isDestroyed = false;
        this.allContainerIds = null;
        this.hiddenSidebarPlacements = new Set();
    }

    init(promoPlacements, ownerId) {
        if (this.isDestroyed) {
            console.error('[PromoPlacementManager] Cannot init destroyed instance');
            return;
        }

        if (!Array.isArray(promoPlacements)) {
            console.error('[PromoPlacementManager] promoPlacements must be an array');
            return;
        }

        if (!ownerId) {
            console.error('[PromoPlacementManager] ownerId is required');
            return;
        }

        this.promoPlacements = promoPlacements;
        this.ownerId = ownerId;
        this.allContainerIds = [...new Set(promoPlacements.map(p => p.containerId))];
        this.setupResizeListener();
    }

    getPlacementType(placementId) {
        if (!placementId) return '';
        const parts = placementId.split('.');
        if (parts.length < 3) return '';
        return parts[2].toLowerCase();
    }

    isSidebarPlacement(placementId) {
        const type = this.getPlacementType(placementId);
        return type === PLACEMENT_TYPES.BR1 || type === PLACEMENT_TYPES.BR2;
    }

    updateFluidWidth() {
        const searchView = document.querySelector('.forSearchView');
        if (!searchView) return;

        const hasVisibleSidebars = this.activePlacements.some(p =>
            this.isSidebarPlacement(p.placementId) &&
            p.isCreated &&
            !this.hiddenSidebarPlacements.has(p.placementId)
        );

        if (hasVisibleSidebars) {
            searchView.classList.add(HAS_PROMO_CLASS);
        } else {
            searchView.classList.remove(HAS_PROMO_CLASS);
        }
    }

    handleSidebarError(placementId) {
        this.hiddenSidebarPlacements.add(placementId);
        this.updateFluidWidth();
    }

    hasEnoughWidthForSidebars() {
        return window.innerWidth >= MIN_SCREEN_WIDTH_FOR_SIDEBARS;
    }

    setupResizeListener() {
        if (this.isResizeListenerAdded || this.isDestroyed) return;

        this.resizeHandler = () => {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = setTimeout(() => {
                this.handleResize();
            }, this.resizeDebounceDelay);
        };

        window.addEventListener('resize', this.resizeHandler);
        this.isResizeListenerAdded = true;
    }

    handleResize() {
        if (this.activePlacements.length === 0 || this.isDestroyed) return;

        const hasWidth = this.hasEnoughWidthForSidebars();

        this.activePlacements.forEach(placement => {
            if (!this.isSidebarPlacement(placement.placementId)) return;

            const container = document.querySelector(`#${placement.containerId}`);
            if (!container) return;

            if (!placement.isCreated && hasWidth) {
                const success = this.adFoxHelper.create(
                    placement.containerId,
                    placement.customParam1,
                    placement.customParam2,
                    this.ownerId,
                    placement.placementId,
                    (plId) => this.handleSidebarError(plId)
                );
                if (success) {
                    placement.isCreated = true; // eslint-disable-line no-param-reassign
                }
            } else if (placement.isCreated) {
                const isHidden = container.style.display === 'none';
                if (hasWidth && isHidden) {
                    this.adFoxHelper.show(placement.containerId);
                } else if (!hasWidth && !isHidden) {
                    this.adFoxHelper.hide(placement.containerId);
                }
            }
        });
    }

    onSearchStart(tourCriteria) {
        if (this.isDestroyed) return;

        try {
            const newCountryId = this.extractCountryId(tourCriteria);

            if (!newCountryId) {
                console.warn('[PromoPlacementManager] Country ID not found');
                return;
            }

            const newId = Number(newCountryId);
            if (this.currentCountryId === newId) {
                return;
            }

            if (this.currentCountryId !== null) {
                this.destroyPlacements();
            }
            this.currentCountryId = newId;

            this.createPlacementsForCountry(newId);
        } catch (error) {
            console.error('[PromoPlacementManager] Error in onSearchStart:', error);
        }
    }

    extractCountryId(tourCriteria) {
        try {
            const countries = tourCriteria.get('countries');

            if (!countries || countries.length === 0) {
                return null;
            }

            const firstCountry = countries.at(0);
            const countryId = firstCountry.get('id');

            return countryId || null;

        } catch (error) {
            console.error('[PromoPlacementManager] Error extracting country ID:', error);
            return null;
        }
    }

    clearAllContainers() {
        this.allContainerIds.forEach(containerId => {
            this.adFoxHelper.clear(containerId);
        });
    }

    destroyPlacements() {
        this.activePlacements.forEach(placement => {
            this.adFoxHelper.clear(placement.containerId);
        });
        this.activePlacements = [];
        this.hiddenSidebarPlacements.clear();
        this.clearAllContainers();
        this.updateFluidWidth();
    }

    createPlacementsForCountry(countryId) {
        if (this.isDestroyed) return;

        const id = Number(countryId);
        const placementsForCountry = this.promoPlacements.filter(p => Number(p.countryId) === id);

        if (placementsForCountry.length === 0) {
            this.clearAllContainers();
            this.updateFluidWidth();
            return;
        }

        const hasWidth = this.hasEnoughWidthForSidebars();

        placementsForCountry.forEach(placement => {
            this.createPlacement(placement, hasWidth);
        });

        this.updateFluidWidth();
    }

    createPlacement(placement, hasWidthForSidebars = true) {
        if (this.isDestroyed) return;

        const { placementId, containerId, customParam1, customParam2 } = placement;
        const container = document.querySelector(`#${containerId}`);

        if (!container) {
            console.warn(`[PromoPlacementManager] Container ${containerId} not found`);
            return;
        }

        if (!customParam1 || !customParam2) {
            console.error(`[PromoPlacementManager] Missing customParam1 or customParam2 for ${placementId}`, {
                customParam1,
                customParam2,
                customParam1Type: typeof customParam1,
                customParam2Type: typeof customParam2
            });
            return;
        }

        const isSidebar = this.isSidebarPlacement(placementId);

        if (isSidebar && !hasWidthForSidebars) {
            this.activePlacements.push({
                ...placement,
                isCreated: false
            });
            return;
        }

        const success = this.adFoxHelper.create(
            containerId,
            customParam1,
            customParam2,
            this.ownerId,
            placementId,
            (plId) => this.handleSidebarError(plId)
        );

        if (success) {
            this.activePlacements.push({
                ...placement,
                isCreated: true
            });
        } else {
            console.error(`[PromoPlacementManager] Failed to create ${placementId}`);
        }
    }

    getCurrentCountryId() {
        return this.currentCountryId;
    }

    getActivePlacements() {
        return [...this.activePlacements];
    }

    reset() {
        this.destroyPlacements();
        this.currentCountryId = null;
    }

    destroy() {
        if (this.isDestroyed) return;

        this.destroyPlacements();

        if (this.resizeHandler && this.isResizeListenerAdded) {
            window.removeEventListener('resize', this.resizeHandler);
            this.isResizeListenerAdded = false;
            this.resizeHandler = null;
        }

        clearTimeout(this.resizeDebounceTimer);

        const searchView = document.querySelector('.forSearchView');
        if (searchView) {
            searchView.classList.remove(HAS_PROMO_CLASS);
        }

        this.currentCountryId = null;
        this.promoPlacements = [];
        this.allContainerIds = null;
        this.ownerId = null;
        this.hiddenSidebarPlacements.clear();
        this.isDestroyed = true;
    }
}

let promoPlacementManagerInstance = null;

export default {
    getInstance() {
        if (!promoPlacementManagerInstance) {
            promoPlacementManagerInstance = new PromoPlacementManager();
        }
        return promoPlacementManagerInstance;
    },

    resetInstance() {
        if (promoPlacementManagerInstance) {
            promoPlacementManagerInstance.destroy();
            promoPlacementManagerInstance = null;
        }
    }
};
